import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Task as TaskIcon,
  StickyNote2 as NoteIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format, isPast, isToday } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ProjectStatusBadge } from './ProjectStatusBadge';
import type { ProjectWithRelations } from '../../types/project';

interface ProjectCardProps {
  project: ProjectWithRelations;
  onEdit: (project: ProjectWithRelations) => void;
  onDelete: (project: ProjectWithRelations) => void;
  onStatusChange?: (project: ProjectWithRelations) => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onEdit,
  onDelete,
  onStatusChange,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCardClick = () => {
    navigate(`/projects/${project._id}`);
  };

  const handleAction = (action: () => void) => {
    handleMenuClose();
    action();
  };

  const clientName = project.clientId
    ? `${project.clientId.firstName} ${project.clientId.lastName}`
    : null;

  const isOverdue = project.dueDate && isPast(new Date(project.dueDate)) && project.status !== 'completed';
  const isDueToday = project.dueDate && isToday(new Date(project.dueDate));

  const progress = project.progress || 0;
  const taskCount = project.taskCount || 0;
  const completedTaskCount = project.completedTaskCount || 0;
  const noteCount = project.noteCount || 0;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        borderLeft: project.color ? `4px solid ${project.color}` : undefined,
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ flex: 1, pb: 1 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <ProjectStatusBadge
            status={project.status}
            onClick={onStatusChange ? () => onStatusChange(project) : undefined}
          />
          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Name */}
        <Typography
          variant="h6"
          fontWeight={600}
          gutterBottom
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {project.name}
        </Typography>

        {/* Description */}
        {project.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              mb: 1.5,
            }}
          >
            {project.description}
          </Typography>
        )}

        {/* Client */}
        {clientName && (
          <Chip
            icon={<PersonIcon />}
            label={clientName}
            size="small"
            variant="outlined"
            sx={{ mb: 1.5 }}
          />
        )}

        {/* Progress */}
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {progress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                backgroundColor: progress === 100 ? 'success.main' : 'primary.main',
              },
            }}
          />
        </Box>

        {/* Stats */}
        <Stack direction="row" spacing={2}>
          <Tooltip title={`${completedTaskCount}/${taskCount} tasks`}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <TaskIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {completedTaskCount}/{taskCount}
              </Typography>
            </Stack>
          </Tooltip>
          <Tooltip title={`${noteCount} notes`}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <NoteIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">
                {noteCount}
              </Typography>
            </Stack>
          </Tooltip>
        </Stack>

        {/* Due Date */}
        {project.dueDate && (
          <Stack direction="row" alignItems="center" spacing={0.5} mt={1.5}>
            <CalendarIcon fontSize="small" color={isOverdue ? 'error' : isDueToday ? 'warning' : 'action'} />
            <Typography
              variant="caption"
              color={isOverdue ? 'error.main' : isDueToday ? 'warning.main' : 'text.secondary'}
              fontWeight={isOverdue || isDueToday ? 600 : 400}
            >
              {isOverdue ? 'Overdue: ' : isDueToday ? 'Due today: ' : 'Due: '}
              {format(new Date(project.dueDate), 'MMM d, yyyy')}
            </Typography>
          </Stack>
        )}

        {/* Tags */}
        {project.tags.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} mt={1.5}>
            {project.tags.slice(0, 3).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            ))}
            {project.tags.length > 3 && (
              <Chip
                label={`+${project.tags.length - 3}`}
                size="small"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            )}
          </Stack>
        )}
      </CardContent>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => handleAction(() => onEdit(project))}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleAction(() => onDelete(project))} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default ProjectCard;
